import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';
import { EmptyState, PageHeader } from '@routes-plan/ui';
import { currentLang, useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { ScreenTourButton } from '../../components/help/ScreenHelp';

export interface HelpMeta {
  topic: string;
  title: string;
  audience: string;
  platforms: string;
  duration: string;
  updated: string;
  hasVideo?: boolean;
}

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Centro de ayuda: guías paso a paso y videos. Solo con sesión; el API las entrega a usuarios de una empresa. */
const HelpList: React.FC = () => {
  const t = useT();
  const [guides, setGuides] = useState<HelpMeta[] | null>(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  setPageTitle('help.title');

  useEffect(() => {
    axios
      .get('help', { params: { lang: currentLang() } })
      .then(({ data }) => setGuides(data))
      .catch(() => setError(true));
  }, []);

  const shown = useMemo(() => {
    if (!guides) return [];
    const words = normalize(q).split(/\s+/).filter(Boolean);
    return guides.filter((g) => words.every((w) => normalize(`${g.title} ${g.audience} ${g.platforms}`).includes(w)));
  }, [guides, q]);

  return (
    <Box sx={{ minWidth: 0, width: '100%', maxWidth: 1600, mx: 'auto', pb: 4 }}>
        <PageHeader title={t('help.title')} description={t('help.subtitle')} helpAction={<ScreenTourButton />} />
        <TextField
          id="help-search"
          data-tour="help-search"
          placeholder={t('help.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="small"
          sx={{ width: '100%', maxWidth: 480, mb: 3 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          inputProps={{ 'aria-label': t('help.search') }}
        />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{t('help.loadError')}</Alert>}
        <Box data-tour="help-guides" sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' } }}>
          {!guides && !error && [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={140} />)}
          {shown.map((g) => (
            <Card key={g.topic} variant="outlined" sx={{ height: '100%' }}>
              <CardActionArea component={RouterLink} to={`/ayuda/${g.topic}`} sx={{ height: '100%', alignItems: 'stretch' }}>
                <CardContent sx={{ display: 'grid', gap: 1, alignContent: 'start', height: '100%' }}>
                  <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {g.title}
                  </Typography>
                  {g.audience && (
                    <Typography variant="body2" color="text.secondary">
                      {g.audience}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {g.hasVideo && <Chip size="small" icon={<PlayCircleOutline />} label={t('help.video')} color="primary" variant="outlined" />}
                    {g.platforms && <Chip size="small" label={g.platforms} />}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
        {guides && !shown.length && <EmptyState title={t('help.empty')} icon={<SearchIcon fontSize="inherit" />} />}
    </Box>
  );
};

export default HelpList;
